import React, { useState } from "react";
import { object, string, number, date, InferType } from 'yup';

import apiService from "../../../services/ApiService";
import snakeToPascal from "../../../tools";
import CustomDatePicker from "../Input/reusables/CustomDatePicker";
import dayjs from "dayjs";
import TailwindSelect from "../../Tailwind/TailwindSelect";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";


export default function SensitivityCustomizableTimingReferenceField({ item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const [timingChoicesItems, setTimingChoicesItems] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        setOptionsLoading(true);
        apiService.getTimingOptions(field.id).then(async (response) => {
            if (response) {
                const data = await response.json();
                setTimingChoicesItems(data);

                // Once options are loaded, resolve whether the current timing is a reference or custom
                const currentTimingId = (sensitivityField) ? sensitivityField.value.value.timing_id : field.currentValue.value.timing_id;
                const matchedItem = data.find((item: any) => item.id == currentTimingId);
                if (matchedItem) {
                    setTimingChoice(matchedItem);
                }
            }
            setOptionsLoading(false);
        });
    }, [field.id]);

    const initialTiming = (sensitivityField) ? sensitivityField.value.value.timing : field.currentValue.value.timing;
    const initialTimingId = (sensitivityField) ? sensitivityField.value.value.timing_id : field.currentValue.value.timing_id;

    const getTimingById = (timingChoices, timingId) => {
        let ret = 'null';
        timingChoices.map((item: any) => {
            if (item.id == timingId) {
                ret = item;
            }
        })
        return ret;
    }

    const initialItem = getTimingById(timingChoicesItems, initialTimingId);


    const [timingId, setTimingId] = React.useState(initialTimingId);
    const [timingChoice, setTimingChoice] = React.useState(initialItem as any);
    const [timing, setTiming] = React.useState(initialTiming);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    timing.start_date.value = (timing.start_date.value) ? dayjs(timing.start_date.value) : null;
    timing.end_date.value = (timing.end_date.value) ? dayjs(timing.end_date.value) : null;
    timing.periodicity.value = (timing.periodicity.value) ? timing.periodicity.value : 'null';

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    const originalChoice = getTimingById(timingChoicesItems, field.currentValue.value.timing_id);
                    setTimingChoice(originalChoice)
                    setTiming(field.currentValue.value.timing);
                    invalidateQueries();
                }
            }
        )
        
    }

    const updateTimingChoice = (value, sensitivityField, _sensitivityFieldJustInitialized) => {

        const apiValue = (value !== 'null') ? value.id : null;
        
        if (value == 'null') {
            triggerSave();
            apiService.postSensitivityTimingValue(sensitivityField.id, 'timing').then(async(response) => {
                if (response) {
                    const timing = await response.json()

                    timing.current_value.periodicity.value = (timing.current_value.periodicity.value) ? timing.current_value.periodicity.value : 'null';

                    sensitivityField.value.value = timing.current_value;
                    sensitivityField.value.value.timing_id = timing.id

                    setTimingId(timing.id);
                    setTimingChoice(value);
                    setTiming(formatNewTiming(timing));

                    resolveSave();
                    invalidateQueries();

                }
            }
            )
        }
        else {
            triggerSave();
            apiService.putSensitivityValue(sensitivityField.id, apiValue).then(
                async(response) => {
                    if (response) {
                            sensitivityField.value.value = value;
                            sensitivityField.value.value.timing_id = value.id

                            setTimingChoice(value);
                            resolveSave();
                            invalidateQueries();

                    }
                }
            )
        }
    }


    const handleTimingChoiceChange = (e) => {
        const newValue = e.target.value;

        if (sensitivityField) {
            updateTimingChoice(newValue, sensitivityField, false)
        } else {
            apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
                async(response) => {
                    if (response) {
                        const newSensitivityField = await response.json()

                        item.sensitivityModelData.fields[field.name] = newSensitivityField
                        updateTimingChoice(newValue, newSensitivityField, true)

                    }
                }
            )
        }
    }

    const checkComplete = (timing) => {
        if (timing.start_date.value && timing.end_date.value) {
            if (timing.start_date.value.diff(timing.end_date.value, 'day') >= 0) {
                field.complete = false;
                field.timingError = 'Start date must be before End date.'
            }
            else if (timing.start_date.value.diff(timing.end_date.value, 'month') == 0) {
                field.complete = false;
                field.timingError = 'Difference Between Dates should be at least one month.'
            }
            else if (timing.periodicity.value !== 'null') {
                field.complete = true;
                field.timingError = null;
            }
            else {
                field.timingError = null;
                field.complete = false;
            }
        }
        else {
            field.complete = false;
            field.timingError = null;
        }
    }

    const formatNewTiming = (response) => {
        return {
            start_date: {
                value: response.start_date ? dayjs(response.start_date) : null,
                address: null,
            },
            end_date: {
                value: response.end_date ? dayjs(response.end_date) : null,
                address: null,
            },
            periodicity: {
                value: response.periodicity,
                address: null,
            },
        }
    }


    const handleChangeStartDate = (newValue, timingId) => {

        triggerSave();
        apiService.putModelDataTimingValue(timingId,
            newValue.format('YYYY-MM-DD'),
            (timing.end_date.value) ? timing.end_date.value.format('YYYY-MM-DD') : null,
            'monthly',
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())

                field.start_dateStore.isOpen = false;
                checkComplete(newTiming);
                setTiming(newTiming);
                resolveSave();
                invalidateQueries();
            }
        })

    };

    const handleChangeStartDatewithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangeStartDate(newValue, timingId);
        } else {
            createSensitivityAndValue(newValue, handleChangeStartDate)
        }
    }

    const handleChangeEndDate = (newValue, timingId) => {
        triggerSave();
        apiService.putModelDataTimingValue(timingId,
            (timing.start_date.value) ? timing.start_date.value.format('YYYY-MM-DD') : null,
            newValue.format('YYYY-MM-DD'),
            'monthly',
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())
                field.end_dateStore.isOpen = false;
                checkComplete(newTiming);
                setTiming(newTiming);
                resolveSave();
                invalidateQueries();
            }
        })
    };

    const handleChangeEndDatewithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangeEndDate(newValue, timingId);
        } else {
            createSensitivityAndValue(newValue, handleChangeEndDate)
        }
    }

    const createSensitivityAndValue = (newValue, handleChangeCallBack) => {
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
            async(response) => {
                if (response) {
                    const newSensitivityField = await response.json()

                    item.sensitivityModelData.fields[field.name] = newSensitivityField

                    apiService.postSensitivityTimingValue(newSensitivityField.id, 'timing').then(
                        async(response) => {
                            if (response) {
                                const timing = await response.json()

                                timing.current_value.periodicity.value = (timing.current_value.periodicity.value) ? timing.current_value.periodicity.value : 'null';

                                sensitivityField.value.value = timing.current_value;
                                sensitivityField.value.value.timing_id = timing.id

                                handleChangeCallBack(newValue, timing.id);
                                setTimingId(timing.id);
                                invalidateQueries();
                            }
                        }
                    )

                }
            }
        )
    }


    // Prepare options for TailwindSelect
    const timingSelectOptions = [
        {
            value: 'null',
            label: 'Custom',
            isItalic: true,
        },
        ...timingChoicesItems.map((e: any) => ({
            value: e,
            // The scenario-level global timing is a virtual reference (id 'global').
            label: e.id === 'global'
                ? 'Global Timing'
                : `${e.parent_name} | ${snakeToPascal(e.field_name)}`,
        }))
    ];

    const handleSelectChange = (value: any) => {
        handleTimingChoiceChange({ target: { value } });
    };

    return (
        <div className="flex flex-row">
            <div>
                {!isReadOnly && (
                    <button
                        onClick={resetSensitivityValue}
                        disabled={!sensitivityField}
                        className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                    </button>
                )}
            </div>
            <div className="flex flex-row">
                <div className="mt-0.5 -mb-1.5">
                    <TailwindSelect
                        value={timingChoice}
                        onChange={handleSelectChange}
                        options={timingSelectOptions}
                        disabled={isReadOnly}
                        className="w-[200px]"
                        getOptionLabel={(option) => {
                            if (option.value === 'null') {
                                return option.label;
                            }
                            const item = option.value;
                            if (item.id === 'global') {
                                return 'Global Timing';
                            }
                            return (
                                <span>
                                    <b className="mr-1">{item.parent_name}</b>
                                    <span className="mr-1 text-slate-400">|</span>
                                    {snakeToPascal(item.field_name)}
                                </span>
                            ) as any;
                        }}
                    />
                </div>
                <SaveIndicator status={saveStatus} className="ml-2 mt-1" />
                {/* Start/End date are always shown once options are loaded. When a
                    timing reference is selected (`timingChoice !== 'null'`) the dates
                    come from the referenced timing option and are displayed read-only;
                    only the custom case is editable. */}
                {!optionsLoading && (() => {
                    const isCustomTiming = timingChoice === 'null';
                    const refTiming: any = isCustomTiming ? null : timingChoice;
                    const startCurrentValue = isCustomTiming
                        ? ((timing.start_date && timing.start_date.value) ? timing.start_date.value : null)
                        : (refTiming?.start_date ? dayjs(refTiming.start_date) : null);
                    const endCurrentValue = isCustomTiming
                        ? ((timing.end_date && timing.end_date.value) ? timing.end_date.value : null)
                        : (refTiming?.end_date ? dayjs(refTiming.end_date) : null);
                    return (
                    <div className="ml-4 -mt-7">
                        <div className="mb-3.5">
                            <div className="text-sm text-slate-700 mb-1">
                                Start Date
                            </div>
                            <CustomDatePicker
                                item={field.start_dateStore}
                                currentValue={startCurrentValue}
                                handleChange={handleChangeStartDatewithSensitivity}
                                readOnly={!isCustomTiming}
                            />
                        </div>
                        <div className="mb-3.5">
                            <div className="text-sm text-slate-700 mb-1">
                                End Date
                            </div>
                            <CustomDatePicker
                                item={field.end_dateStore}
                                currentValue={endCurrentValue}
                                handleChange={handleChangeEndDatewithSensitivity}
                                readOnly={!isCustomTiming}
                            />
                        </div>
                        {isCustomTiming && field.timingError && (
                            <div className="text-red-600 text-sm">
                                {field.timingError}
                            </div>
                        )}
                    </div>
                    );
                })()}
            </div>
        </div>
    )
}
